import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import type { RecentConsultation } from '../types';

interface RecentConsultationsTableProps {
  consultations: RecentConsultation[];
  onReExecute: (id: string) => void;
}

const RecentConsultationsTable = ({ consultations, onReExecute }: RecentConsultationsTableProps) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<keyof RecentConsultation>('consultationDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: keyof RecentConsultation) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedConsultations = [...consultations].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortDirection === 'asc' 
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime();
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });

  const getStatusColor = (status: RecentConsultation['status']) => {
    const colors = {
      success: 'text-success bg-success/10',
      error: 'text-error bg-error/10',
      pending: 'text-warning bg-warning/10',
      warning: 'text-warning bg-warning/10'
    };
    return colors[status];
  };

  const getStatusIcon = (status: RecentConsultation['status']) => {
    const icons = {
      success: 'CheckCircle2',
      error: 'XCircle',
      pending: 'Clock',
      warning: 'AlertTriangle'
    };
    return icons[status];
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            Consultas Recentes
          </h3>
          <Button
            variant="outline"
            size="sm"
            iconName="History"
            iconPosition="left"
            onClick={() => navigate('/consultation-history')}
          >
            Ver Histórico Completo
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('companyName')}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Empresa
                  <Icon 
                    name={sortField === 'companyName' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('cnpj')}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  CNPJ
                  <Icon 
                    name={sortField === 'cnpj' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('consultationDate')}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Data/Hora
                  <Icon 
                    name={sortField === 'consultationDate' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                    color="currentColor" 
                  />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">
                Status
              </th>
              <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">
                Produtos
              </th>
              <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">
                Total Tributos
              </th>
              <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedConsultations.map((consultation) => (
              <tr 
                key={consultation.id}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/historical-consultation-detail?id=${consultation.id}`)}
              >
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-foreground">
                    {consultation.companyName}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-muted-foreground font-mono">
                    {consultation.cnpj}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(consultation.consultationDate)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${getStatusColor(consultation.status)}`}>
                    <Icon 
                      name={getStatusIcon(consultation.status)} 
                      size={14} 
                      color="currentColor" 
                    />
                    <span className="text-xs font-medium">
                      {consultation.statusLabel}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="text-sm font-medium text-foreground">
                    {consultation.productCount}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="text-sm font-semibold text-foreground font-mono">
                    {formatCurrency(consultation.totalTax)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => navigate(`/historical-consultation-detail?id=${consultation.id}`)}
                      aria-label="Ver detalhes"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RefreshCw"
                      onClick={() => onReExecute(consultation.id)}
                      aria-label="Reexecutar consulta"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentConsultationsTable;